import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import AppContext from "../context/AppContext";

const DEFAULT_CHILD_AGE = 3;

const MultiItemSelectDropdown = ({ field, label, items, data = {}, settings = {}, onChange, validity = "", ...rest }) => {
   const { registerPopupListener, popupCloseEvent } = useContext(AppContext);
   const [show, setShow] = useState(false);
   const inputRef = useRef();
   const initData = {};
   items.forEach((i) => {
      const rawValue = data.hasOwnProperty(i.name) ? data[i.name] : 0;
      initData[i.name] = Number.isFinite(rawValue) ? rawValue : parseInt(rawValue, 10);
   });

   const minChildAge = Number.isFinite(settings?.minChildAge)
     ? settings.minChildAge
     : 0;
   const searchChildAge = Number.isFinite(settings?.searchChildAge)
      ? settings.searchChildAge
      : 0;

   const childrenAgesOptions = useMemo(() => {
      return Array.from({ length: searchChildAge - minChildAge + 1 }, (_, i) => minChildAge + i);
   }, [minChildAge, searchChildAge]);

   const defaultChildrenAge = useMemo(() => {
      if (
        DEFAULT_CHILD_AGE >= minChildAge &&
        DEFAULT_CHILD_AGE <= searchChildAge
      ) {
        return DEFAULT_CHILD_AGE;
      }

      if (!childrenAgesOptions.length) {
        return 0;
      }

      return childrenAgesOptions[0];
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [childrenAgesOptions]);

   const buildChildrenAgesFromPassengers = (passengers = []) => {
      const ages = [];
      const resAges = [];

      passengers.forEach((passenger) => {
         if (!passenger || !passenger.child) return;

         if (passenger.resident) {
            resAges.push(passenger.age);
         } else {
            ages.push(passenger.age);
         }
      });

      return { ages, resAges };
   };

   const syncAges = (existing, targetCount, fallbackAge) => {
      const trimmed = existing.slice(0, targetCount);

      while (trimmed.length < targetCount) {
         trimmed.push(fallbackAge);
      }

      return trimmed;
   };

   const initialPassengers = buildChildrenAgesFromPassengers(data.passengers || []);
   const initialChildrenAges = syncAges(initialPassengers.ages, initData.children || 0, defaultChildrenAge);
   const initialChildrenResAges = syncAges(initialPassengers.resAges, initData.childrenRes || 0, defaultChildrenAge);

   const [selectData, setSelectData] = useState(initData);
   const [childrenAges, setChildrenAges] = useState(initialChildrenAges);
   const [childrenResAges, setChildrenResAges] = useState(initialChildrenResAges);

   const selectDataRef = useRef(selectData);
   const childrenAgesRef = useRef(childrenAges);
   const childrenResAgesRef = useRef(childrenResAges);

   useEffect(() => {
      selectDataRef.current = selectData;
   }, [selectData]);

   useEffect(() => {
      childrenAgesRef.current = childrenAges;
   }, [childrenAges]);

   useEffect(() => {
      childrenResAgesRef.current = childrenResAges;
   }, [childrenResAges]);

   const handleChange = useCallback(
      (updatedSelectData, nextChildrenAges = childrenAgesRef.current, nextChildrenResAges = childrenResAgesRef.current) => {
         const passengers = [
            ...nextChildrenAges.map((age) => ({ age, child: true, resident: false })),
            ...nextChildrenResAges.map((age) => ({ age, child: true, resident: true })),
         ];

         onChange &&
            onChange({
               ...updatedSelectData,
               passengers,
            });
      },
      [onChange]
   );

   const handleIncrDecr = (ev, pos) => {
      let { name, value } = ev.target.parentElement.querySelector("input");
      let val = parseInt(value);
      val = pos ? val + 1 : val - 1;
      val = val >= 0 ? val : 0;
      let updatedParams = {
         ...selectData,
         [name]: val,
      };

      let nextChildrenAges = childrenAges;
      let nextChildrenResAges = childrenResAges;

      if (name === "children") {
         nextChildrenAges = syncAges(childrenAges, val, defaultChildrenAge);
         setChildrenAges(nextChildrenAges);
      } else if (name === "childrenRes") {
         nextChildrenResAges = syncAges(childrenResAges, val, defaultChildrenAge);
         setChildrenResAges(nextChildrenResAges);
      }

      setSelectData(updatedParams);

      handleChange(updatedParams, nextChildrenAges, nextChildrenResAges);
   };
   const handleApply = (ev) => {
      handleChange(selectData);
      setShow(false);
   };
   const muteAllClicks = (ev) => {
      ev.stopPropagation();
      ev.preventDefault();
   };
   const handleShowHide = (ev) => {
      ev.stopPropagation();
      ev.preventDefault();
      popupCloseEvent();
      setShow(true);
   };
   useEffect(() => {
      document.addEventListener("click", (ev) => {
         setShow(false);
      });
      registerPopupListener(() => {
         setShow(false);
      });
   }, [registerPopupListener]);

   useEffect(() => {
      let hasAdults = false;
      for (let i = 0; i < items.length; i++) {
         let f = items[i];
         if (f.isAdult) {
            if (data[f.name]) {
               hasAdults = true;
               break;
            }
         }
      }
      let v = "";
      if (!hasAdults) {
         v = validity;
      }
      inputRef.current && inputRef.current.setCustomValidity(v);
   }, [data, validity, items]);

   const values = Object.entries(data)
      .filter(([_, val]) => parseInt(val) > 0)
      .map(([_, val]) => {
         return parseInt(val);
      })
      .reduce((prev, curr) => prev + curr, 0);

   const handleAgeChange = useCallback(
      (index, isResident) =>
         (ev) => {
            const parsed = parseInt(ev.target.value, 10);
            const nextAge = Number.isFinite(parsed) ? parsed : "";
            if (isResident) {
               const currentChildrenResAges = childrenResAgesRef.current;

               if (currentChildrenResAges[index] === nextAge) return;

               const nextChildrenResAges = currentChildrenResAges.map((age, idx) => (idx === index ? nextAge : age));

               setChildrenResAges(nextChildrenResAges);
               handleChange(selectDataRef.current, childrenAgesRef.current, nextChildrenResAges);
            } else {
               const currentChildrenAges = childrenAgesRef.current;

               if (currentChildrenAges[index] === nextAge) return;

               const nextChildrenAges = currentChildrenAges.map((age, idx) => (idx === index ? nextAge : age));

               setChildrenAges(nextChildrenAges);
               handleChange(selectDataRef.current, nextChildrenAges, childrenResAgesRef.current);
            }
         },
      [handleChange]
   );

   const renderChildAgeItems = (ages, isResident) => {
      return ages.map((age, idx) => (
         <li
            key={`${isResident ? 'child-res-age' : 'child-age'}-${idx}`}
            className={`child-age${idx % 2 === 0 ? ' even' : ' odd'}${idx <= 1 ? ' child-age-first-row' : ''}`}
         >
            <div className='child-age-wrapper'>
              <div className='description'>
                <p>Child {idx + 1} age</p>
              </div>
              <div className='count'>
                <select value={age} onChange={handleAgeChange(idx, isResident)}>
                  <option disabled defaultValue=''>Select Age</option>
                  {childrenAgesOptions.map((optAge) => (
                    <option key={`${isResident ? 'child-res-age-opt' : 'child-age-opt'}-${optAge}`} value={optAge}>
                      {optAge} yrs
                    </option>
                  ))}
                </select>
              </div>
            </div>
         </li>
      ));
   };

   return (
      <div className='field'>
         <label className='multi-items-select pos-relative' id='multi-items-select'>
            <span>{label}</span>
            <div className='select-wrap'>
               <input
                  ref={inputRef}
                  onClick={handleShowHide}
                  {...rest}
                  name={field}
                  className={`select-trigger${values ? " valued" : ""}`}
                  value={values ? `${values}x` : ""}
                  placeholder='Please select'
                  readOnly={show}
                  onChange={() => console.log()}
               />
               <ul
                  className={`multi-items-dropdown${show ? " show" : ""}`}
                  id='multi-items-dropdown'
                  onClick={muteAllClicks}
               >
                  {items.map((i, idx) => (
                     <React.Fragment key={idx}>
                        <li>
                           <div className='description'>
                              <p>{i.label}</p>
                              <span>{i.hint}</span>
                           </div>
                           <div className='count'>
                              <button type='button' onClick={(ev) => handleIncrDecr(ev, false)}>
                                 -
                              </button>
                              <input
                                 {...rest}
                                 name={i.name}
                                 type='text'
                                 min='0'
                                 value={selectData.hasOwnProperty(i.name) ? selectData[i.name] : 0}
                                 onChange={() => console.log()}
                                 readOnly
                              />
                              <button type='button' onClick={(ev) => handleIncrDecr(ev, true)}>
                                 +
                              </button>
                           </div>
                        </li>
                        {i.name === 'children' && childrenAges.length
                          ? renderChildAgeItems(childrenAges, false)
                          : null}
                        {i.name === 'childrenRes' && childrenResAges.length
                           ? renderChildAgeItems(childrenResAges, true)
                           : null}
                     </React.Fragment>
                  ))}
                  <li className='flex'>
                     <button type='button' className='btn btn-apply flex w-full' onClick={handleApply}>
                        Apply
                     </button>
                  </li>
               </ul>
            </div>
         </label>
      </div>
   );
};

export default MultiItemSelectDropdown;
